/**
 * Returns the Admin Dashboard Content page component.
 * 
 * @returns The Admin Dashboard Content page component.
 */

import { Fragment, useState, useEffect } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import Logo from "../../../images/logo.png"

import {
  HiHome,
  HiUsers,
  HiClipboardList,
  HiOutlineX,
  HiMenu,
  HiClipboardCheck,
  HiDocumentText,
  HiArrowLeft
} from "react-icons/hi";

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

export default function SideNavigation() {
  const [navigationTabs, setNavigationTabs] = useState([
    { name: 'Dashboard', href: '/admin', icon: HiHome, current: false },
    { name: 'Project Logs', href: '/admin/project-logs', icon: HiClipboardList, current: false },
    { name: 'Project Approval', href: '/admin/project-approval', icon: HiClipboardCheck, current: false },
    { name: 'DOI Requests', href: '/admin/doi-requests', icon: HiDocumentText, current: false },
    { name: 'User Management', href: '/admin/user-management', icon: HiUsers, current: false },
    { name: 'Visualization Approval', href: '/admin/visualization-approval', icon: HiClipboardCheck, current: false },
  ])
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    currentOpenTab();
  }, []);

  /**
   * Handles finding the current tab selected on the side nav.
   */
  function currentOpenTab() {
    // Find the navigation tab that matches the current link
    const currentNavIndex = navigationTabs.findIndex((navigationTab) => navigationTab.href === window.location.pathname);
    
    // Only update if a matching tab was found
    if (currentNavIndex >= 0) {
      // Mark the navigation tab as the current one
      const updatedNav = { ...navigationTabs[currentNavIndex], current: true };
      // Update the navigationTabs list with the updated navigation tab
      const newNavs = [
        ...navigationTabs.slice(0, currentNavIndex),
        updatedNav,
        ...navigationTabs.slice(currentNavIndex + 1)
      ];
      setNavigationTabs(newNavs);
    }
  }

  return (
    <>
      {/* <div className="h-full"> */}
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50 lg:hidden" onClose={setSidebarOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/80" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                    <button type="button" className="-m-2.5 p-2.5" onClick={() => setSidebarOpen(false)}>
                      <span className="sr-only">Close sidebar</span>
                      <HiOutlineX className="h-6 w-6 text-white" aria-hidden="true" />
                    </button>
                  </div>
                </Transition.Child>
                {/* Sidebar component, swap this element with another sidebar if you like */}
                <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-2">
                  <div className="flex h-16 shrink-0 items-center">
                    <img
                      className="h-8 w-auto"
                      src={Logo}
                      alt="Arctic Research Foundation Logo"
                    />
                  </div>
                  <nav className="flex flex-1 flex-col">
                    <ul role="list" className="flex flex-1 flex-col gap-y-7">
                      <li>
                        <ul role="list" className="-mx-2 space-y-2">
                          {navigationTabs.map((item) => {
                            const IconComponent = item.icon;
                            if (!IconComponent) {
                              console.error(`Icon not found for navigation item: ${item.name}`);
                              return null;
                            }
                            return (
                              <li key={item.name}>
                                <a
                                  href={item.href}
                                  className={classNames(
                                    item.current
                                      ? 'bg-gray-50 text-blue-600'
                                      : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50',
                                    'group flex gap-x-3 rounded-md p-3 leading-6 font-semibold'
                                  )}
                                >
                                  <IconComponent
                                    className={classNames(
                                      item.current ? 'text-blue-600' : 'text-gray-400 group-hover:text-blue-600',
                                      'h-6 w-6 shrink-0'
                                    )}
                                    aria-hidden="true"
                                  />
                                  {item.name}
                                </a>
                              </li>
                            );
                          })}
                        </ul>
                      </li>
                      <li className="mt-auto mb-4">
                        <ul role="list" className="-mx-2 space-y-2">
                          <a
                            href="/"
                            className="text-gray-700 hover:text-blue-600 hover:bg-gray-50 group flex gap-x-3 rounded-md p-3 leading-6 font-semibold"
                          >
                            <HiArrowLeft
                              className="text-gray-400 group-hover:text-blue-600 h-6 w-6 shrink-0"
                              aria-hidden="true"
                            />
                            Back to Database
                          </a>

                        </ul>
                      </li>
                    </ul>
                  </nav>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Static sidebar for desktop */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
        {/* Sidebar component, swap this element with another sidebar if you like */}
        <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 bg-white px-6">
          <div className="flex mt-3 h-16 shrink-0 items-center">
            <img
              className="h-10 w-auto"
              src={Logo}
              alt="Arctic Research Foundation Logo"
            />
          </div>
          <nav className="flex flex-1 flex-col">
            <ul role="list" className="flex flex-1 flex-col gap-y-7">
              <li>
                <ul role="list" className="-mx-2 space-y-2">
                  {navigationTabs.map((item) => {
                    const IconComponent = item.icon;
                    if (!IconComponent) {
                      console.error(`Icon not found for navigation item: ${item.name}`);
                      return null;
                    }
                    return (
                      <li key={item.name}>
                        <a
                          href={item.href}
                          className={classNames(
                            item.current
                              ? 'bg-gray-50 text-blue-600'
                              : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50',
                            'group flex gap-x-3 rounded-md p-3 leading-6 font-semibold'
                          )}
                        >
                          <IconComponent
                            className={classNames(
                              item.current ? 'text-blue-600' : 'text-gray-400 group-hover:text-blue-600',
                              'h-6 w-6 shrink-0'
                            )}
                            aria-hidden="true"
                          />
                          {item.name}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </li>
              <li className="mt-auto mb-4">
                <ul role="list" className="-mx-2 space-y-2">
                  <a
                    href="/"
                    className="text-gray-700 hover:text-blue-600 hover:bg-gray-50 group flex gap-x-3 rounded-md p-3 leading-6 font-semibold"
                  >
                    <HiArrowLeft
                      className="text-gray-400 group-hover:text-blue-600 h-6 w-6 shrink-0"
                      aria-hidden="true"
                    />
                    Back to Database
                  </a>

                </ul>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      <div className="sticky top-0 z-40 flex items-center gap-x-6 bg-white px-4 py-4 shadow-sm sm:px-6 lg:hidden">
        <button type="button" className="-m-2.5 p-2.5 text-gray-700 lg:hidden" onClick={() => setSidebarOpen(true)}>
          <span className="sr-only">Open sidebar</span>
          <HiMenu className="h-6 w-6" aria-hidden="true" />
        </button>
        <div className="flex-1 font-semibold leading-6 text-gray-900">Dashboard</div>
      </div>
    </>
  )
}